import { useEffect, useState } from 'react';
import { operatorConsole, PoolCategoryConfig } from '../../lib/api/operatorConsole';
import { ImageSlot } from '../Configs/ImageSlot';
import { NameField } from './NameField';
import { OrderField } from './OrderField';

export function CategoryConfigsPage() {
  const [configs, setConfigs] = useState<PoolCategoryConfig[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void loadConfigs();
  }, []);

  // silent: refresh the grid in place, without the "Loading categories…" swap.
  async function loadConfigs(silent = false) {
    try {
      if (!silent) setLoading(true);
      // Backend lists only categories with at least one pack enabled for this
      // operator — the same set the iframe pill row shows.
      const result = await operatorConsole.listPoolCategoryConfigs();
      setConfigs(result.pool_category_configs);
    } catch (err) {
      setError(`Failed to load categories: ${err instanceof Error ? err.message : err}`);
    } finally {
      setLoading(false);
    }
  }

  function replaceConfig(updated: PoolCategoryConfig) {
    setConfigs((prev) =>
      prev.map((c) => (c.pool_category_id === updated.pool_category_id ? updated : c)),
    );
  }

  async function handleNameSave(poolCategoryId: number, name: string) {
    try {
      setError(null);
      const updated = await operatorConsole.patchPoolCategoryConfig(poolCategoryId, { name });
      replaceConfig(updated);
      flashSuccess(name === '' ? 'Name reset' : 'Name saved');
    } catch (err) {
      setError(`Name update failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  async function handlePositionSave(poolCategoryId: number, position: number | null) {
    try {
      setError(null);
      const updated = await operatorConsole.patchPoolCategoryConfig(poolCategoryId, { position });
      replaceConfig(updated);
      flashSuccess(position === null ? 'Order reset' : 'Order saved');
      // The backend owns the effective order (ordered categories first, the
      // rest in default order), so re-fetch instead of re-sorting here.
      await loadConfigs(true);
    } catch (err) {
      setError(`Order update failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  async function handleImageUpload(poolCategoryId: number, file: File) {
    try {
      setError(null);
      const updated = await operatorConsole.uploadPoolCategoryConfigImage(poolCategoryId, file);
      replaceConfig(updated);
      flashSuccess('Image uploaded');
    } catch (err) {
      setError(`Upload failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  async function handleImageDelete(poolCategoryId: number) {
    try {
      setError(null);
      const updated = await operatorConsole.deletePoolCategoryConfigImage(poolCategoryId);
      replaceConfig(updated);
      flashSuccess('Image reset');
    } catch (err) {
      setError(`Reset failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  function flashSuccess(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 2500);
  }

  return (
    <div className="p-6">
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <div>
          <h1 className="text-[20px] font-semibold text-[var(--co-text)]">Categories</h1>
          <p className="text-[12px] text-[var(--co-muted)]">
            Per-operator category name, image and order overrides — leave empty to fall back to
            defaults. Lower order shows first in the iframe.
          </p>
        </div>
        {(error || success) && (
          <span
            className={`rounded px-2 py-1 text-[11px] font-medium ${
              error
                ? 'bg-[var(--co-error)] text-white'
                : 'bg-[var(--co-success)] text-white'
            }`}
          >
            {error ?? success}
          </span>
        )}
      </div>

      {loading ? (
        <p className="text-[13px] text-[var(--co-muted)]">Loading categories…</p>
      ) : configs.length === 0 ? (
        <p className="text-[13px] text-[var(--co-muted)]">
          No categories available for this operator.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {configs.map((config) => (
            <div
              key={config.pool_category_id}
              className="rounded-xl border border-[var(--co-border)] bg-[var(--co-surface)] p-3"
            >
              <div className="mb-2 flex items-center gap-2">
                <span className="font-mono text-[11px] text-[var(--co-muted)]">
                  #{config.pool_category_id}
                </span>
                <NameField
                  name={config.name}
                  defaultName={config.pool_category_name}
                  onSave={(name) => handleNameSave(config.pool_category_id, name)}
                  onReset={() => handleNameSave(config.pool_category_id, '')}
                />
                {config.has_overrides && (
                  <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                    Customized
                  </span>
                )}
              </div>

              <div className="mb-2">
                <OrderField
                  position={config.position}
                  onSave={(position) => handlePositionSave(config.pool_category_id, position)}
                  onReset={() => handlePositionSave(config.pool_category_id, null)}
                />
              </div>

              <ImageSlot
                label="Image"
                imageUrl={config.image_url ?? config.pool_category_image_url}
                isOverride={config.image_override}
                onUpload={(file) => handleImageUpload(config.pool_category_id, file)}
                onClear={() => handleImageDelete(config.pool_category_id)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
